import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  installOpenOsV2HistoryBridge,
  uninstallOpenOsV2HistoryBridge,
} from '../utils/openOsV2HistoryBridge';
import OpenOsV2ProductionChart from './OpenOsV2ProductionChart';
import OpenOsV2App from '../open-os-v2/OpenOsV2App';
import './OpenOsV2MockOverlay.css';

const MemoOpenOsV2App = React.memo(OpenOsV2App);

function isEllipTruncated(el) {
  if (!el) return false;
  if (el.scrollWidth > el.clientWidth + 1) return true;
  const parent = el.parentElement;
  if (parent && parent.scrollWidth > parent.clientWidth + 1) {
    const cs = window.getComputedStyle(el);
    if (cs.textOverflow === 'ellipsis' || cs.overflow === 'hidden' || cs.overflowX === 'hidden') {
      return el.scrollWidth >= el.clientWidth;
    }
  }
  return false;
}

/**
 * Overlay só do Histórico do equipamento (mesmo dialog do Abrir OS v2).
 * Usado a partir do OrderDetailsModalV2 — não monta o fluxo de abertura.
 */
export default function OpenOsV2EquipmentHistoryOverlay({
  isOpen,
  onClose,
  serie = '',
  modelo = '',
  patrimonio = '',
  instalado = null,
  zIndex = 27000,
}) {
  const hostRef = useRef(null);
  const bridgesRef = useRef(false);
  const prodChartKeyRef = useRef('');
  const [prodChartHost, setProdChartHost] = useState(null);
  const [prodChartData, setProdChartData] = useState(null);
  const [cellTip, setCellTip] = useState(null);

  if (isOpen && !bridgesRef.current) {
    bridgesRef.current = true;
    installOpenOsV2HistoryBridge();
  } else if (!isOpen && bridgesRef.current) {
    bridgesRef.current = false;
  }

  useEffect(() => {
    if (!isOpen) {
      setProdChartHost(null);
      setProdChartData(null);
      setCellTip(null);
      window.__openOsV2SetProductionChart = undefined;
      uninstallOpenOsV2HistoryBridge();
      return undefined;
    }

    let cancelled = false;
    installOpenOsV2HistoryBridge();

    window.__openOsV2SetProductionChart = (payload) => {
      queueMicrotask(() => {
        if (cancelled) return;
        if (!payload || payload.clear) {
          prodChartKeyRef.current = '';
          setProdChartData(null);
          return;
        }
        const cicloRaw = payload.cicloMensal;
        const cicloN = cicloRaw != null && cicloRaw !== '' ? Math.trunc(Number(cicloRaw)) : null;
        const next = {
          meses: Array.isArray(payload.meses) ? payload.meses : [],
          series: Array.isArray(payload.series) ? payload.series : [],
          activeKeys: Array.isArray(payload.activeKeys) ? payload.activeKeys : [],
          cicloMensal: Number.isFinite(cicloN) && cicloN > 0 ? cicloN : null,
        };
        const key = JSON.stringify(next);
        if (key === prodChartKeyRef.current) return;
        prodChartKeyRef.current = key;
        setProdChartData(next);
      });
    };

    const syncProdHost = () => {
      if (cancelled) return;
      const el = document.getElementById('oosv2-prod-recharts-host');
      setProdChartHost((prev) => (prev === el ? prev : el || null));
    };
    const mo = new MutationObserver(syncProdHost);
    mo.observe(document.body, { childList: true, subtree: true });
    syncProdHost();

    return () => {
      cancelled = true;
      mo.disconnect();
      window.__openOsV2SetProductionChart = undefined;
      setProdChartHost(null);
      setProdChartData(null);
      uninstallOpenOsV2HistoryBridge();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setCellTip(null);
      return undefined;
    }

    const hide = () => setCellTip(null);

    const onOver = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const host = hostRef.current;
      const el = t.closest('.oosv2-ellip');
      if (!el || !host || !host.contains(el)) {
        hide();
        return;
      }
      if (el.classList.contains('oosv2-hist-tipo-badge')) {
        hide();
        return;
      }
      if (!isEllipTruncated(el)) {
        hide();
        return;
      }
      const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text || text === '—') {
        hide();
        return;
      }
      const r = el.getBoundingClientRect();
      const tipW = Math.min(320, window.innerWidth - 24);
      let left = r.left + r.width / 2;
      left = Math.max(tipW / 2 + 8, Math.min(left, window.innerWidth - tipW / 2 - 8));
      const placeBelow = r.top < 48;
      setCellTip({
        text,
        top: placeBelow ? r.bottom + 8 : Math.max(8, r.top - 8),
        left,
        placeBelow,
      });
    };

    const onOut = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const el = t.closest('.oosv2-ellip');
      if (!el) return;
      const rel = e.relatedTarget;
      if (rel instanceof Node && el.contains(rel)) return;
      hide();
    };

    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    document.addEventListener('scroll', hide, true);
    return () => {
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      document.removeEventListener('scroll', hide, true);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const serieClean = String(serie || '').trim();
  if (!serieClean) return null;

  return createPortal(
    <div
      className="open-os-v2-mock-overlay open-os-v2-equip-hist-overlay"
      style={{ zIndex }}
      role="presentation"
    >
      <div ref={hostRef} className="open-os-v2-mock-host" data-open-os-v2-host="1" data-oosv2-hist-only="1">
        <MemoOpenOsV2App
          historyOnly
          onClose={onClose}
          historyEquip={{
            serie: serieClean,
            modelo: String(modelo || '').trim() || '—',
            patrimonio: String(patrimonio || '').trim() || '—',
            instalado: instalado || null,
          }}
        />
      </div>
      {cellTip ? (
        <div
          className={'oosv2-table-tip' + (cellTip.placeBelow ? ' is-below' : '')}
          style={{ top: cellTip.top, left: cellTip.left }}
          role="tooltip"
        >
          {cellTip.text}
        </div>
      ) : null}
      {prodChartHost && prodChartData
        ? createPortal(
            <OpenOsV2ProductionChart
              meses={prodChartData.meses}
              series={prodChartData.series}
              activeKeys={prodChartData.activeKeys}
              cicloMensal={prodChartData.cicloMensal}
            />,
            prodChartHost,
          )
        : null}
    </div>,
    document.body,
  );
}
